import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Trash2, Paperclip, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, download } from "@/lib/api";
import { rp, tglID, todayISO } from "@/lib/format";
import { Card, Empty } from "@/components/admin/common";
import { accName } from "@/components/finance/shared";
import EntryForm from "@/components/finance/EntryForm";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";

const COLOR = { masuk: "text-emerald-700", keluar: "text-rose-700", pindah: "text-sky-700" };
const SIGN = { masuk: "+", keluar: "-", pindah: "↔ " };

export default function TransactionList({ accounts, taxMode }) {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const latest = useRef(0);
  const load = useCallback(() => {
    const [y, m] = month ? month.split("-").map(Number) : [];
    const params = month ? { start: `${month}-01`, end: `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`, q } : { q };
    const n = ++latest.current;
    return api.get("/admin/finance/transactions", { params }).then((r) => { if (n === latest.current) setItems(r.data); });
  }, [month, q]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/admin/finance/transactions/${del.id}`);
      toast.success("Transaksi dihapus");
      setDel(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const totals = (items || []).reduce((t, x) => ({ ...t, [x.kind]: (t[x.kind] || 0) + x.amount }), {});

  return (
    <div className="space-y-4" data-testid="transaction-list">
      <div className="flex flex-wrap gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-12 w-44" data-testid="tx-month-input" />
        <div className="relative flex-1 min-w-[180px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari keterangan / no. bukti" className="h-12 pl-9" data-testid="tx-search-input" /></div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Card className="p-3"><p className="text-xs text-slate-500">Uang masuk</p><p className="font-extrabold text-emerald-700" data-testid="tx-total-masuk">{rp(totals.masuk)}</p></Card>
        <Card className="p-3"><p className="text-xs text-slate-500">Uang keluar</p><p className="font-extrabold text-rose-700" data-testid="tx-total-keluar">{rp(totals.keluar)}</p></Card>
        <Card className="p-3"><p className="text-xs text-slate-500">Selisih</p><p className="font-extrabold">{rp((totals.masuk || 0) - (totals.keluar || 0))}</p></Card>
      </div>
      {items && !items.length && <Empty text="Belum ada transaksi di bulan ini." testid="tx-empty" />}
      <div className="space-y-2">
        {items?.map((t) => (
          <Card key={t.id} className="p-3 flex items-center gap-3" data-testid={`tx-row-${t.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{t.description || accName(accounts, t.category_account)}</p>
              <p className="text-xs text-slate-500 truncate">{tglID(t.date)} · {t.ref} · {accName(accounts, t.category_account)} · {accName(accounts, t.cash_account)}</p>
            </div>
            {t.receipt_url && <button onClick={() => download(t.receipt_url.replace("/api", ""))} className="p-2 text-slate-500" aria-label="Bukti" data-testid={`tx-receipt-${t.id}`}><Paperclip className="w-4 h-4" /></button>}
            <p className={`font-extrabold whitespace-nowrap ${COLOR[t.kind]}`}>{SIGN[t.kind]}{rp(t.amount)}</p>
            <button onClick={() => setEdit(t)} className="p-2 text-slate-400 hover:text-ink" aria-label="Ubah" data-testid={`tx-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => setDel(t)} className="p-2 text-slate-400 hover:text-rose-600" aria-label="Hapus" data-testid={`tx-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="font-admin max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ubah transaksi {edit?.ref}</DialogTitle></DialogHeader>
          {edit && <EntryForm accounts={accounts} taxMode={taxMode} initial={edit} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="font-admin">
          <AlertDialogHeader><AlertDialogTitle>Hapus transaksi {del?.ref}?</AlertDialogTitle>
            <AlertDialogDescription>Status pembayaran booking/invoice yang terhubung akan dihitung ulang.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600" data-testid="tx-confirm-delete">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
